import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import {
  Figure,
  LoadMore,
  NameLink,
  TABLE_REGION,
  TD,
  TD_MUTED,
  TD_NUM,
  TH,
  TH_NUM,
  THEAD,
  caseHref,
  directorHref,
} from '@/pages/cvm/shared';
import { fetchCvmVotes } from '@/services/cvm';
import type { CvmDissentRow, CvmPairRow } from '@/services/types';

const PAGE_SIZE = 50;

/**
 * The directors' recorded positions.
 *
 * An extrato names the relator, then who followed whom, who dissented and on
 * what, who was recused, who presided. The reach panel says how many judgments
 * carry a vote record and a named bench; the positions strip counts rows per
 * position; the pairs table counts, per pair of directors, the judgments both
 * sat on and how often the record puts them on the same side -- a count over
 * shared judgments, never a rate, because nine judgments in ten are unanimous
 * and a rate would restate that base. The dissent index lists every minuted
 * departure with the clause it was read from.
 */
export function CvmVotesPage() {
  const { t, i18n } = useTranslation(['cvm', 'common']);
  const { data, isPending, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['cvm', 'votes'],
      queryFn: ({ pageParam }) => fetchCvmVotes({ limit: PAGE_SIZE, offset: pageParam }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((n, page) => n + page.dissents.length, 0);
        return loaded < lastPage.dissents_total ? loaded : undefined;
      },
    });
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const first = data?.pages[0];
  const dissents = data?.pages.flatMap((page) => page.dissents) ?? [];
  const number = new Intl.NumberFormat(i18n.language);

  if (isPending) return <SkeletonLoader rows={8} />;
  if (error) return <ErrorMessage message={error.message} onRetry={() => void refetch()} />;
  if (!first) return <SkeletonLoader rows={8} />;

  const positions = Object.entries(first.positions).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cvm:votes.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cvm:votes.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cvm:votes.explainer" />
      <AsOfStrip asOf={first.as_of} />

      <section className="rounded-lg border bg-card p-4" data-reach>
        <h2 className="mb-2 text-sm font-semibold text-foreground">{t('cvm:votes.reach.title')}</h2>
        <Answer answer={first.reach} help={t('cvm:votes.reach.help')}>
          {(reach) => (
            <>
              <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Figure
                  label={t('cvm:votes.reach.decisions')}
                  value={number.format(reach.decisions)}
                />
                <Figure
                  label={t('cvm:votes.reach.withVotes')}
                  value={number.format(reach.with_votes)}
                />
                <Figure
                  label={t('cvm:votes.reach.withBench')}
                  value={number.format(reach.with_named_participants)}
                />
                <Figure label={t('cvm:votes.reach.rows')} value={number.format(reach.rows)} />
              </dl>
              <dl className="mt-2 grid grid-cols-3 gap-2">
                <Figure
                  label={t('cvm:votes.reach.dissents')}
                  value={number.format(reach.dissents)}
                />
                <Figure
                  label={t('cvm:votes.reach.majorityItems')}
                  value={number.format(reach.majority_items)}
                />
                <Figure
                  label={t('cvm:votes.reach.unanimousItems')}
                  value={number.format(reach.unanimous_items)}
                />
              </dl>
            </>
          )}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4" data-positions>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('cvm:votes.positions.title')}
        </h2>
        <p className="mb-2 text-xs text-muted-foreground">{t('cvm:votes.positions.help')}</p>
        <dl className="flex flex-wrap gap-2 [&>div]:min-w-[10rem] [&>div]:flex-1">
          {positions.map(([code, count]) => (
            <Figure
              key={code}
              label={t(`cvm:positions.${code}`, { defaultValue: code })}
              value={number.format(count)}
            />
          ))}
        </dl>
      </section>

      <section className="rounded-lg border bg-card p-4" data-pairs>
        <h2 className="mb-2 text-sm font-semibold text-foreground">{t('cvm:votes.pairs.title')}</h2>
        <Answer answer={first.pairs} help={t('cvm:votes.pairs.help')}>
          {(pairs: CvmPairRow[]) => (
            <div className="max-h-96 overflow-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className={THEAD}>
                  <tr>
                    <th className={TH}>{t('cvm:votes.pairs.pair')}</th>
                    <th className={TH_NUM}>{t('cvm:votes.pairs.shared')}</th>
                    <th className={TH_NUM}>{t('cvm:votes.pairs.sameSide')}</th>
                    <th className={TH_NUM}>{t('cvm:votes.pairs.oppositeSide')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pairs.map((pair) => (
                    <tr key={`${pair.slug_a}-${pair.slug_b}`} className="border-t">
                      <td className={TD}>
                        <NameLink name={pair.director_a} href={directorHref(pair.slug_a)} />
                        <span className="text-muted-foreground"> · </span>
                        <NameLink name={pair.director_b} href={directorHref(pair.slug_b)} />
                      </td>
                      <td className={TD_NUM}>{number.format(pair.shared)}</td>
                      <td className={TD_NUM}>{number.format(pair.same_side)}</td>
                      <td className={TD_NUM}>{number.format(pair.opposite_side)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Answer>
      </section>

      <section className="space-y-3" data-dissents>
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">{t('cvm:votes.dissents.title')}</h2>
          <ExportButton path="/api/v1/cvm/export/votes/dissents" />
        </div>
        <p
          className="text-xs text-muted-foreground"
          aria-live="polite"
          data-total={first.dissents_total}
        >
          {t('cvm:list.showing', {
            loaded: number.format(dissents.length),
            total: number.format(first.dissents_total),
          })}
        </p>
        <div
          ref={scrollRef}
          className={TABLE_REGION}
          role="region"
          aria-label={t('cvm:votes.dissents.tableLabel')}
        >
          <table className="w-full text-sm">
            <thead className={THEAD}>
              <tr>
                <th className={TH}>{t('cvm:votes.dissents.columns.process')}</th>
                <th className={TH}>{t('cvm:votes.dissents.columns.relator')}</th>
                <th className={TH}>{t('cvm:votes.dissents.columns.voter')}</th>
                <th className={TH}>{t('cvm:votes.dissents.columns.position')}</th>
                <th className={TH}>{t('cvm:votes.dissents.columns.evidence')}</th>
              </tr>
            </thead>
            <tbody>
              {dissents.map((row, index) => (
                <DissentRow key={`${row.case_key}-${index}`} row={row} />
              ))}
            </tbody>
          </table>
          <div ref={sentinelRef} aria-hidden className="h-px" />
          <LoadMore
            hasNextPage={Boolean(hasNextPage)}
            isFetchingNextPage={isFetchingNextPage}
            loadMore={loadMore}
            total={first.dissents_total}
            pageSize={PAGE_SIZE}
          />
        </div>
      </section>

      <FieldGuide i18nKey="cvm:votes.guide" />
    </div>
  );
}

function DissentRow({ row }: { row: CvmDissentRow }) {
  const { t } = useTranslation(['cvm']);
  return (
    <tr className="border-t align-top hover:bg-muted/30">
      <td className={TD}>
        <Link
          to={caseHref(row.case_key)}
          className="text-primary underline-offset-2 hover:underline"
        >
          {row.process_number}
        </Link>
        <div className="text-xs text-muted-foreground">{row.judgment_date ?? '—'}</div>
      </td>
      <td className={TD}>{row.relator ?? '—'}</td>
      <td className={TD}>
        <NameLink name={row.voter_canonical ?? row.voter} href={directorHref(row.voter_slug)} />
      </td>
      <td className={TD_MUTED}>
        <span className="text-foreground">
          {t(`cvm:positions.${row.position}`, { defaultValue: row.position })}
        </span>
        {' · '}
        {t(`cvm:voteScopes.${row.scope}`, { defaultValue: row.scope })}
        {row.item_seq ? ` · ${t('cvm:case.walk.item', { seq: row.item_seq })}` : ''}
      </td>
      <td className={TD_MUTED}>{row.evidence_span ?? '—'}</td>
    </tr>
  );
}
